/**
 * WordPress API Services
 *
 * Fetches data directly from the WordPress REST API without a Node.js middleware.
 * Uses the `_fields` parameter to request ONLY the essential data needed by the
 * frontend, reducing payload size and improving performance.
 *
 * All TypeScript interfaces are in: src/types/api.types.ts
 * All data-transformation helpers are in: src/utils/helpers.ts
 */

import axios from 'axios';
import config from '../config/config';
import { transformPost } from '../utils/helpers';
import toastService from '../utils/toastService';
import type {
  WPPost,
  PostsResponse,
  SinglePostResponse,
  CategoriesResponse,
  ContactFormData,
  CommentsResponse,
  WPComment,
  CommentSubmission,
  CommentSubmitResult,
} from '../types/api.types';

// Re-export all types so consumers can import from one place
export type {
  WPPost,
  WPCategory,
  PostsResponse,
  SinglePostResponse,
  CategoriesResponse,
  ContactFormData,
  WPComment,
  CommentsResponse,
  CommentSubmission,
  CommentSubmitResult,
} from '../types/api.types';

const API_BASE_URL = config.WP_BASE;

// Create Axios Instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// Request Interceptor: Attach JWT Token dynamically
api.interceptors.request.use(
  (config: any) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: any) => Promise.reject(error)
);

// Response Interceptor: Catch 401 Unauthorized globally
api.interceptors.response.use(
  (response: any) => response,
  (error: any) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    return Promise.reject(error);
  }
);

// ─── SERVICE NAMESPACES ───────────────────────────────────────────────────────

/**
 * Articles / Posts Management
 */
export const ArticleService = {
  getAll: async (
    options: {
      categoryId?: number | null;
      search?: string;
      perPage?: number;
      page?: number;
    } = {}
  ): Promise<PostsResponse> => {
    try {
      const params: Record<string, any> = {
        _embed: 'true',
        _fields: 'id,title,excerpt,date,slug,link,_links,_embedded,featured,meta',
        per_page: options.perPage || 100,
      };

      if (options.categoryId) params.categories = options.categoryId;
      if (options.search) params.search = options.search;
      if (options.page) params.page = options.page;

      const response = await api.get('/wp/v2/posts', { params });

      const data = response.data;
      const total = response.headers['x-wp-total'] || data.length;
      const totalPages = response.headers['x-wp-totalpages'] || 1;

      return {
        success: true,
        data: data.map(transformPost),
        total: Number(total),
        totalPages: Number(totalPages),
        page: options.page || 1,
      };
    } catch (error: any) {
      throw error?.response?.data?.message || error.message || 'Failed to fetch posts';
    }
  },

  getHomePageData: async (): Promise<any> => {
    try {
      const response = await api.get('/fir/v1/homepage');
      return { success: true, data: response.data };
    } catch (error: any) {
      throw error?.response?.data?.message || error.message || 'Failed to fetch homepage data';
    }
  },

  getPaginatedRecipes: async (options: { page?: number; search?: string; category?: string } = {}): Promise<PostsResponse> => {
    try {
      const params: Record<string, any> = {};
      if (options.page) params.page = options.page;
      if (options.search) params.search = options.search;
      if (options.category) params.category = options.category;

      const response = await api.get('/fir/v1/recipes', { params });
      return response.data; // already formatted by our custom API
    } catch (error: any) {
      throw error?.response?.data?.message || error.message || 'Failed to fetch recipes';
    }
  },

  getById: async (id: number | string): Promise<SinglePostResponse> => {
    try {
      const isNumeric = /^\d+$/.test(String(id));
      let data;
      
      if (isNumeric) {
        const params = {
          _embed: 'true',
          _fields: 'id,title,excerpt,content,date,slug,link,_links,_embedded,featured,meta',
        };
        const response = await api.get(`/wp/v2/posts/${id}`, { params });
        data = response.data;
      } else {
        const params = {
          slug: id,
          _embed: 'true',
          _fields: 'id,title,excerpt,content,date,slug,link,_links,_embedded,featured,meta',
        };
        const response = await api.get(`/wp/v2/posts`, { params });
        if (!response.data || response.data.length === 0) {
          throw new Error('Post not found');
        }
        data = response.data[0];
      }

      const post = transformPost(data);
      const wpDate = data.date;
      const actualId = data.id;

      let related: WPPost[] = [];
      let prev: WPPost | null = null;
      let next: WPPost | null = null;

      try {
        const promises = [];

        // Related posts (same category, excluding current)
        if (post.categoryId) {
          promises.push(
            api
              .get('/wp/v2/posts', {
                params: {
                  _embed: 'true',
                  _fields: 'id,title,excerpt,content,date,slug,link,_links,_embedded,featured,meta',
                  categories: post.categoryId,
                  exclude: actualId,
                  per_page: 8,
                },
              })
              .then((res: any) => res.data)
              .catch(() => [])
          );
        } else {
          promises.push(Promise.resolve([]));
        }

        // Previous post (published before this one)
        promises.push(
          api
            .get('/wp/v2/posts', {
              params: {
                _embed: 'true',
                _fields: 'id,title,excerpt,content,date,slug,link,_links,_embedded,featured,meta',
                before: wpDate,
                per_page: 1,
                order: 'desc',
              },
            })
            .then((res: any) => res.data)
            .catch(() => [])
        );

        // Next post (published after this one)
        promises.push(
          api
            .get('/wp/v2/posts', {
              params: {
                _embed: 'true',
                _fields: 'id,title,excerpt,content,date,slug,link,_links,_embedded,featured,meta',
                after: wpDate,
                per_page: 1,
                order: 'asc',
              },
            })
            .then((res: any) => res.data)
            .catch(() => [])
        );

        const [relData, prevData, nextData] = await Promise.all(promises);

        if (relData.length > 0) related = relData.map(transformPost);
        if (prevData.length > 0) prev = transformPost(prevData[0]);
        if (nextData.length > 0) next = transformPost(nextData[0]);
      } catch (e) {
        // Non-critical
      }

      return { success: true, data: { ...post, related, prev, next } };
    } catch (error: any) {
      throw error?.response?.data?.message || error.message || 'Failed to fetch post';
    }
  },

  getReadNextByArticle: async (articleId: number): Promise<any[]> => {
    try {
      const response = await api.get('/wp/v2/posts', {
        params: {
          per_page: 8,
          exclude: articleId,
          _embed: 'true',
        },
      });
      return response.data;
    } catch {
      return [];
    }
  },

  getRelatedForCategory: async (categoryId: number, excludeId?: number, limit = 8): Promise<any[]> => {
    try {
      const response = await api.get('/wp/v2/posts', {
        params: {
          categories: categoryId,
          exclude: excludeId,
          per_page: limit,
          _embed: 'true',
        },
      });
      return response.data;
    } catch {
      return [];
    }
  },
};

/**
 * Categories Management
 */
export const CategoryService = {
  getAll: async (): Promise<CategoriesResponse> => {
    try {
      const response = await api.get('/wp/v2/categories', {
        params: {
          _fields: 'id,name,slug,description,count,image',
          per_page: 100,
          hide_empty: 'false',
        },
      });

      const formatted = response.data
        .map((cat: any) => ({
          id: cat.id,
          name: cat.name.toUpperCase() === 'UNCATEGORIZED' ? 'MILLETS' : cat.name.toUpperCase(),
          slug: cat.slug === 'uncategorized' ? 'millets' : cat.slug,
          description: cat.description,
          count: cat.count,
          img: cat.image || null, // Will use WP image if uploaded via backend
        }));

      return { success: true, data: formatted, total: formatted.length };
    } catch (error: any) {
      throw error?.response?.data?.message || error.message || 'Failed to fetch categories';
    }
  },
};

/**
 * Contact Form Submissions
 */
export const ContactService = {
  submit: async (data: ContactFormData): Promise<{ success: boolean; message: string }> => {
    const formData = new FormData();
    formData.append('your-name', data.name);
    formData.append('your-email', data.email);
    formData.append('your-subject', data.subject);
    formData.append('your-message', data.message);

    const formId = 183;

    try {
      const response = await api.post(
        `/contact-form-7/v1/contact-forms/${formId}/feedback`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const result = response.data;
      return {
        success: result.status === 'mail_sent',
        message: result.message || 'Something went wrong.',
      };
    } catch (error: any) {
      return {
        success: false,
        message: error?.response?.data?.message || error.message || 'Failed to connect to the server.',
      };
    }
  },
};

/**
 * Comments Management
 */
export const CommentService = {
  getByPostId: async (postId: number | string): Promise<CommentsResponse> => {
    try {
      const response = await api.get('/wp/v2/comments', {
        params: {
          post: String(postId),
          per_page: 100,
          status: 'approve',
          _fields: 'id,parent,author_name,author_avatar_urls,date,content',
        },
      });

      const data = response.data;
      const total = Number(response.headers['x-wp-total'] ?? data.length);

      const mapped: WPComment[] = data.map((c: any) => ({
        id: c.id,
        parent: c.parent ?? 0,
        author_name: c.author_name ?? 'Anonymous',
        author_avatar:
          c.author_avatar_urls?.['48'] ?? c.author_avatar_urls?.['96'] ?? '',
        date: new Date(c.date).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
        content: (c.content?.rendered ?? '').replace(/<[^>]+>/g, '').trim(),
      }));

      return { success: true, data: mapped, total };
    } catch (error: any) {
      throw error?.response?.data?.message || error.message || 'Failed to fetch comments';
    }
  },

  submit: async (data: CommentSubmission): Promise<CommentSubmitResult> => {
    // Client-side validation guard
    if (!data.authorName.trim()) throw new Error('Name is required.');
    if (
      !data.authorEmail.trim() ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.authorEmail)
    )
      throw new Error('A valid email address is required.');
    if (!data.content.trim()) throw new Error('Comment content cannot be empty.');
    if (data.content.trim().length > 5000)
      throw new Error('Comment is too long (max 5000 characters).');

    const body: Record<string, any> = {
      post: Number(data.postId),
      author_name: data.authorName.trim().slice(0, 245),
      author_email: data.authorEmail.trim(),
      content: data.content.trim(),
    };
    if (data.authorUrl?.trim()) body.author_url = data.authorUrl.trim();
    if (data.parent) body.parent = data.parent;

    try {
      const response = await api.post('/wp/v2/comments', body);

      if (response.status === 201)
        return { success: true, pending: false, message: 'Your comment has been posted!' };
      if (response.status === 202)
        return {
          success: true,
          pending: true,
          message: 'Your comment is awaiting moderation. Thank you!',
        };

      throw new Error('Failed to submit comment. Please try again.');
    } catch (error: any) {
      throw new Error(error?.response?.data?.message || error.message || 'Failed to submit comment. Please try again.');
    }
  },
};

export const SponsorService = {
  getAll: async (): Promise<any[]> => {
    try {
      const response = await api.get('/jim/v1/sponsors');
      return Array.isArray(response.data) ? response.data : [];
    } catch {
      return [];
    }
  },
};

